import assetActionService from "../../../../services/assetService/assetActionService/assetActionService";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CACHE_KEY_ASSETACTIONS } from "../../../../utils/constants";
import { AssetActionInterface } from "../../../../models/AssetModel/AssetAction/AssetAction";

interface UpdateAssetActionsContext {
  previousAssetActions: AssetActionInterface[];
}

const useUpdateAssetAction = (page: number, pageSize: number) => {
  const queryClient = useQueryClient();

  return useMutation<
    AssetActionInterface,
    Error,
    AssetActionInterface,
    UpdateAssetActionsContext
  >({
    mutationFn: async (assetAction: AssetActionInterface) => {
      const { id, ...assetActionData } = assetAction;
      return assetActionService.Update(id || "", assetActionData);
    },

    onMutate: async (updatedAssetAction: AssetActionInterface) => {
      await queryClient.cancelQueries({ queryKey: [CACHE_KEY_ASSETACTIONS] });

      const previousAssetActions =
        queryClient.getQueryData<AssetActionInterface[]>([
          CACHE_KEY_ASSETACTIONS,
          page,
          pageSize,
        ]) || [];

      queryClient.setQueryData<AssetActionInterface[]>(
        [CACHE_KEY_ASSETACTIONS, page, pageSize],
        (assetActions = []) =>
          assetActions.map((assetAction) =>
            assetAction.id === updatedAssetAction.id
              ? updatedAssetAction
              : assetAction
          )
      );

      return { previousAssetActions };
    },

    onSuccess: (savedAssetAction) => {
      queryClient.setQueryData<AssetActionInterface[]>(
        [CACHE_KEY_ASSETACTIONS, page, pageSize],
        (assetActions) =>
          assetActions?.map((assetAction) =>
            assetAction.id === savedAssetAction.id
              ? savedAssetAction
              : assetAction
          )
      );
    },

    onError: (error, updatedAssetAction, context) => {
      if (!context) return;

      queryClient.setQueryData<AssetActionInterface[]>(
        [CACHE_KEY_ASSETACTIONS, page, pageSize],
        context.previousAssetActions
      );
    },
  });
};

export default useUpdateAssetAction;
